import type { CapitalGlobalCoverageBenefitOption } from '@/features/quotes/schemas/capital-global/coverages-catalog';
import { Field, Select } from '@noorden-insurtech/alba-design-system';
import { memo, useCallback, useMemo } from 'react';

/* eslint-disable no-unused-vars -- callback parameter names document arity for callers */
type Props = {
  options: CapitalGlobalCoverageBenefitOption[];
  /** Código do benefício escolhido na linha (vazio = nenhum). */
  selectedCode: string;
  onSelectBenefit: (benefitCode: string) => void;
  disabled?: boolean;
  invalid?: boolean;
};
/* eslint-enable no-unused-vars */

/**
 * Seletor do benefício da cobertura — tanto os níveis de capital (ex.: Auxílio
 * Funeral R$ 5.000/7.000/10.000) quanto as opções sem valor monetário (ex.:
 * "30 Diárias de Incapacidade Temporária", "Assistência Funeral Familiar").
 * Substitui o input livre de capital: o código escolhido vai para o form e,
 * no submit, vira `benefits: [...]` da cobertura; quando a opção tem valor em
 * R$, ela também define o capital da linha.
 *
 * Sem estado próprio: a seleção é o `benefitCode` do form — assim hidratação e
 * edição reaproveitam o mesmo canal, e dois níveis de mesmo valor não se
 * confundem.
 *
 * Isolado + memo: o Ark Select é sensível a re-renders do pai (a tabela
 * re-renderiza a cada mudança do form via `form.Subscribe`).
 */
export const CoverageBenefitSelect = memo(function CoverageBenefitSelect({
  options,
  selectedCode,
  onSelectBenefit,
  disabled,
  invalid,
}: Props) {
  const items = useMemo(
    () =>
      options.map(option => ({
        value: option.code,
        label: option.name,
      })),
    [options]
  );

  const handleValueChange = useCallback(
    (details: { value: string[] }) => {
      onSelectBenefit(details.value[0] ?? '');
    },
    [onSelectBenefit]
  );

  return (
    <Field.Root className='min-w-0 w-full' invalid={invalid}>
      <Select.Root
        value={selectedCode ? [selectedCode] : []}
        onValueChange={handleValueChange}
        positioning={{ sameWidth: true }}
        items={items}
        disabled={disabled}
        deselectable
      >
        <Select.Control className='flex w-full'>
          <Select.Trigger className='w-full rounded-xl border border-alba-gray-400 px-5 py-3 text-xs text-alba-gray-600'>
            <Select.ValueText placeholder='Selecione' />
            <Select.Indicator />
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            {items.map(option => (
              <Select.Item key={option.value} item={option}>
                <Select.ItemText>{option.label}</Select.ItemText>
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    </Field.Root>
  );
});
